import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileText, 
  ClipboardList, 
  Award, 
  Users, 
  UserCheck,
  TrendingUp,
  Calendar,
  CheckCircle
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Loader from '../../components/ui/Loader';
import dashboardService from '../../services/dashboardService';
import usePermisos from '../../hooks/usePermisos';

const Dashboard = () => {
  const navigate = useNavigate();
  const { getRutaBase } = usePermisos();
  const rutaBase = getRutaBase();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsuarios: 0,
    usuariosActivos: 0,
    usuariosInactivos: 0,
    usuariosPorRol: {},
    totalProcesos: 0,
    procesosActivos: 0,
    totalPerfiles: 0,
    ultimosProcesos: []
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await dashboardService.getEstadisticas();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
      // Valores por defecto en caso de error
      setStats({
        totalUsuarios: 0,
        usuariosActivos: 0,
        usuariosInactivos: 0,
        usuariosPorRol: {},
        totalProcesos: 0,
        procesosActivos: 0,
        totalPerfiles: 0,
        ultimosProcesos: []
      });
    } finally {
      setLoading(false);
    }
  };

  const statsCards = [
    {
      title: 'Total Usuarios',
      value: stats.totalUsuarios,
      icon: Users,
      color: 'bg-purple-500',
      link: `${rutaBase}/usuarios`
    },
    {
      title: 'Usuarios Activos',
      value: stats.usuariosActivos,
      icon: UserCheck,
      color: 'bg-green-500',
      link: `${rutaBase}/usuarios`
    },
    {
      title: 'Total Procesos',
      value: stats.totalProcesos,
      icon: FileText,
      color: 'bg-[#fe7f2d]',
      link: `${rutaBase}/procesos`
    },
    {
      title: 'Procesos Activos',
      value: stats.procesosActivos,
      icon: CheckCircle,
      color: 'bg-emerald-500',
      link: `${rutaBase}/procesos`
    },
    {
      title: 'Total Perfiles',
      value: stats.totalPerfiles,
      icon: ClipboardList,
      color: 'bg-[#297373]',
      link: `${rutaBase}/perfiles`
    },
    {
      title: 'Usuarios Inactivos',
      value: stats.usuariosInactivos,
      icon: TrendingUp,
      color: 'bg-neutral-400',
      link: `${rutaBase}/usuarios`
    }
  ];

  const quickAccessButtons = [
    { title: 'Gestionar Procesos', link: `${rutaBase}/procesos`, icon: FileText },
    { title: 'Gestionar Usuarios', link: `${rutaBase}/usuarios`, icon: Users }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold text-neutral-800 mb-2">
          PostPsic
        </h1>
        <p className="text-neutral-600">
          Bienvenido al sistema de gestión de procesos de selección
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statsCards.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card 
              onClick={() => navigate(stat.link)}
              className="cursor-pointer hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-600">{stat.title}</p>
                  <p className="text-3xl font-bold text-neutral-800 mt-2">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <h2 className="text-xl font-semibold text-neutral-800 mb-4">
          Acceso Rápido
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickAccessButtons.map((button, index) => (
            <Card
              key={index}
              onClick={() => navigate(button.link)}
              className="cursor-pointer hover:shadow-lg transition-shadow text-center"
            >
              <button.icon className="w-8 h-8 text-[#fe7f2d] mx-auto mb-2" />
              <p className="text-sm font-medium text-neutral-700">{button.title}</p>
            </Card>
          ))}
        </div>
      </motion.div>

      {/* Últimos Procesos */}
      {stats.ultimosProcesos && stats.ultimosProcesos.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <h2 className="text-xl font-semibold text-neutral-800 mb-4">
            Últimos Procesos
          </h2>
          <div className="space-y-3">
            {stats.ultimosProcesos.map((proceso, index) => (
              <Card 
                key={index}
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate(`${rutaBase}/procesos/${proceso.id || ''}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-neutral-800">{proceso.nombre}</h3>
                    <div className="flex flex-wrap gap-3 mt-2 text-sm text-neutral-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Creado: {proceso.fechaCreacion ? new Date(proceso.fechaCreacion).toLocaleDateString('es-PE') : 'N/A'}
                      </span>
                      {proceso.fechaFinalizacion && (
                        <span className="flex items-center gap-1 text-emerald-600">
                          <CheckCircle className="w-4 h-4" />
                          Finalizado: {new Date(proceso.fechaFinalizacion).toLocaleDateString('es-PE')}
                        </span>
                      )}
                      {!proceso.fechaFinalizacion && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          En Curso
                        </span>
                      )}
                    </div>
                    {proceso.usuarioCreador && (
                      <p className="text-xs text-neutral-500 mt-1">
                        Creado por: {proceso.usuarioCreador}
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </motion.div>
      )}

      
    </div>
  );
};

export default Dashboard;
